import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react';

type SetValue<T> = Dispatch<SetStateAction<T>>;

function useLocalStorage<T>(key: string, initialValue: T): [T, SetValue<T>] {
    const readValue = (): T => {
        try {
            const item = window.localStorage.getItem(key);
            return item && item !== 'undefined' ? (JSON.parse(item) as T) : initialValue;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(`Error reading localStorage key “${key}”:`, error);
            return initialValue;
        }
    };

    const [storedValue, setStoredValue] = useState<T>(readValue);

    const setValue: SetValue<T> = useCallback(
        (value) => {
            try {
                setStoredValue((current) => {
                    const newValue = value instanceof Function ? value(current) : value;
                    if (newValue != null && newValue !== 'undefined') {
                        window.localStorage.setItem(key, JSON.stringify(newValue));
                    } else {
                        window.localStorage.removeItem(key);
                    }
                    return newValue;
                });

                window.dispatchEvent(new Event('local-storage'));
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(`Error setting localStorage key “${key}”:`, error);
            }
        },
        [key]
    );

    useEffect(() => {
        const handleStorageChange = () => {
            setStoredValue(readValue());
        };

        handleStorageChange();
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('local-storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('local-storage', handleStorageChange);
        };
        // eslint-disable-next-line
    }, []);

    return [storedValue, setValue];
}

export default useLocalStorage;
